/**
 * Lightweight localStorage helper for AI Workspace UI preferences.
 * Follows the same manual-localStorage pattern as `MENU_IS_OPENED` in
 * `homeSlice` rather than widening redux-persist (which is intentionally
 * scoped to `['user']` for security/PII reasons).
 *
 * Stored shape:
 *   { lastConversationId?, composerDraft?, sidebarExpanded? }
 *
 * `composerDraftByConversationId` is pruned to the last
 * MAX_DRAFT_CONVERSATIONS entries to prevent unbounded storage growth.
 */

import { AI_WORKSPACE_STORAGE_KEY } from './workspaceLayout.constants';

const MAX_DRAFT_CONVERSATIONS = 10;

interface AiWorkspaceStoredState {
  lastConversationId?: number;
  composerDraftByConversationId?: Record<string, string>;
  sidebarExpanded?: boolean;
  sidebarDisplayMode?: 'overlay' | 'docked';
}

const read = (): AiWorkspaceStoredState => {
  try {
    const raw = localStorage.getItem(AI_WORKSPACE_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as AiWorkspaceStoredState) : {};
  } catch {
    return {};
  }
};

const write = (state: AiWorkspaceStoredState): void => {
  try {
    localStorage.setItem(AI_WORKSPACE_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage quota exceeded or private browsing — silently ignore.
  }
};

export const workspaceStorage = {
  getLastConversationId(): number | undefined {
    return read().lastConversationId;
  },

  setLastConversationId(id: number): void {
    write({ ...read(), lastConversationId: id });
  },

  getComposerDraft(conversationId: number): string {
    return read().composerDraftByConversationId?.[String(conversationId)] ?? '';
  },

  setComposerDraft(conversationId: number, draft: string): void {
    const current = read();
    const byId = { ...(current.composerDraftByConversationId ?? {}) };

    if (draft) {
      byId[String(conversationId)] = draft;
    } else {
      delete byId[String(conversationId)];
    }

    // Prune to the most-recently-written MAX_DRAFT_CONVERSATIONS entries.
    const keys = Object.keys(byId);
    if (keys.length > MAX_DRAFT_CONVERSATIONS) {
      const pruned: Record<string, string> = {};
      keys.slice(-MAX_DRAFT_CONVERSATIONS).forEach((k) => {
        pruned[k] = byId[k];
      });
      write({ ...current, composerDraftByConversationId: pruned });
    } else {
      write({ ...current, composerDraftByConversationId: byId });
    }
  },

  getSidebarExpanded(): boolean {
    return read().sidebarExpanded ?? false;
  },

  setSidebarExpanded(expanded: boolean): void {
    write({ ...read(), sidebarExpanded: expanded });
  },

  getSidebarDisplayMode(): 'overlay' | 'docked' {
    return read().sidebarDisplayMode ?? 'overlay';
  },

  setSidebarDisplayMode(mode: 'overlay' | 'docked'): void {
    write({ ...read(), sidebarDisplayMode: mode });
  },
};
